import React, { useState } from 'react';
import {
  Text,
  Button,
  VStack,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import ModalShell from '../../../components/ui/ModalShell';
import { sendExtensionRequestEmail, cancelCollabRemindersAndNotifyClient } from '../../../services/flaskService';
import { updateCollabExtentionRequest } from '../../../services/firebaseService';
import { collabApi } from '../../../services/api/collab';
import { canRejectCollab, canCancelCollab, hasRecentExtensionRequest, hasRecentWaitlistRequest } from '../utils/collabUtils';
import { Collab } from '@foodfluence/shared';

interface ExtensionRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  clientEmail: string;
  clientId: string;
  influencerHandle: string;
  influencerPhone: string;
  collabId: string;
  collabStatus: string;
  isCampaignFull?: boolean;
  endDatePassed?: boolean;
  schedulingFullyBooked?: boolean;
  postByNoSlotsMessage?: string | null; // replaces the generic fully-booked body when go-live is why
  collabEndDate?: string;
  collab?: Collab; // Add collab prop to check recent requests
}

const ExtensionRequestModal: React.FC<ExtensionRequestModalProps> = ({ 
  isOpen, 
  onClose, 
  clientName, 
  clientEmail, 
  clientId, 
  influencerHandle, 
  influencerPhone,
  collabId,
  collabStatus,
  isCampaignFull,
  endDatePassed,
  schedulingFullyBooked,
  postByNoSlotsMessage,
  collabEndDate,
  collab
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [action, setAction] = useState<'extension' | 'reject' | 'cancel' | null>(null);
  const toast = useToast();

  // Check if requests were made recently
  const hasRecentExtension = collab ? hasRecentExtensionRequest(collab) : false;
  const hasRecentWaitlist = collab ? hasRecentWaitlistRequest(collab) : false;
  const hasRecentRequest = hasRecentExtension || hasRecentWaitlist;

  const handleRequestExtension = async () => {
    // Don't allow duplicate requests within a week
    if (hasRecentRequest) {
      const requestType = hasRecentWaitlist ? 'waitlist' : 'extension';
      toast({
        title: 'Request Already Sent',
        description: `You've already requested a ${requestType} within the last week. Please wait for the brand to respond.`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setAction('extension');
    setIsSubmitting(true);
    try {
      const success = await sendExtensionRequestEmail(
        collabId,
        isCampaignFull
      );

      if (success) {
        await updateCollabExtentionRequest(collabId, !!isCampaignFull, !isCampaignFull);

        const message = isCampaignFull 
          ? "Your waitlist request has been sent to the brand. They will review and get back to you soon."
          : "Your request for an extension has been sent to the brand. They will review and get back to you soon.";
        
        toast({
          title: isCampaignFull ? "Waitlist Request Sent" : "Extension Request Sent",
          description: message,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
      } else {
        throw new Error("Failed to send extension request");
      }
    } catch (error) {
      console.error('Error sending extension request:', error);
      toast({
        title: "Error",
        description: "Failed to send extension request. Please try again or contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setAction(null);
    }
  };

  const handleRejectCollab = async () => {
    setAction('reject');
    setIsSubmitting(true);
    try {
      await collabApi.declineCollab({ collabId, reason: "Campaign end date passed" });
      toast({
        title: "Collab Rejected",
        description: "You have rejected this collab due to the campaign end date passing.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      console.error('Error rejecting collab:', error);
      toast({
        title: "Error",
        description: "Failed to reject the collab. Please try again or contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setAction(null);
    }
  };

  const handleCancelCollab = async () => {
    setAction('cancel');
    setIsSubmitting(true);
    try {
      await collabApi.cancelCollab({ collabId, reason: "Campaign end date passed" });
      cancelCollabRemindersAndNotifyClient(
        collabId,
        clientId,
        clientEmail,
        clientName,
        influencerHandle,
        influencerPhone
      );

        toast({
          title: "Collab Canceled",
          description: "You have canceled this collab due to the campaign end date passing. The brand has been notified.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
    } catch (error) {
      console.error('Error canceling collab:', error);
      toast({
        title: "Error",
        description: "Failed to cancel the collab. Please try again or contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setAction(null);
    }
  };

  const canReject = canRejectCollab(collabStatus);
  const canCancel = canCancelCollab(collabStatus);

  const buttonText = isCampaignFull ? "Request Waitlist" : "Request Extension";

  const modalTitle = hasRecentRequest
    ? (hasRecentWaitlist ? "Waitlist Already Requested" : "Extension Already Requested")
    : schedulingFullyBooked
      ? (postByNoSlotsMessage ? "No Visit Times Left" : "All Time Slots Booked")
      : (isCampaignFull && endDatePassed ? "Campaign Full & Ended" : isCampaignFull ? "Campaign Full" : "Campaign Ended");

  const modalBody = hasRecentRequest
    ? `You've already requested a ${hasRecentWaitlist ? 'waitlist' : 'extension'} for this collab within the last week. Please wait for the brand to respond.`
    : schedulingFullyBooked
      ? (postByNoSlotsMessage ?? `All available time slots for the campaign from ${clientName} are currently booked. Request an extension so the brand can add more availability.`)
      : (isCampaignFull && endDatePassed
          ? `The campaign from ${clientName} is full and ended on ${collabEndDate}.`
          : isCampaignFull
            ? `The campaign from ${clientName} is now full.`
            : `The campaign from ${clientName} ended on ${collabEndDate}.`);

  const linkButtonStyle = {
    as: 'button' as const,
    fontSize: '14px',
    fontWeight: 600,
    color: '#0E0E0E',
    textDecoration: 'underline',
    cursor: 'pointer',
    minH: '44px',
    _hover: { color: '#55555A' },
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      footer={
        <Button
          mt="20px"
          w="full"
          h="52px"
          borderRadius="999px"
          border="1.5px solid #0E0E0E"
          bg="#FFFFFF"
          color="#0E0E0E"
          fontSize="16px"
          fontWeight="600"
          _hover={{ bg: '#F2F2F0' }}
          onClick={onClose}
          disabled={isSubmitting}
        >
          Close
        </Button>
      }
    >
      <VStack spacing="16px" align="stretch">
        <Text fontSize="16px" color="#3E3E42" lineHeight="1.45">
          {modalBody}
        </Text>

        <Button
          bg={hasRecentRequest ? '#ECECEA' : '#C6F432'}
          color={hasRecentRequest ? '#6E6E72' : '#0E0E0E'}
          _hover={{ bg: hasRecentRequest ? '#ECECEA' : '#B8E62A' }}
          _active={{ bg: hasRecentRequest ? '#ECECEA' : '#B8E62A' }}
          _disabled={{ opacity: 1, cursor: 'not-allowed' }}
          onClick={handleRequestExtension}
          disabled={isSubmitting || hasRecentRequest}
          leftIcon={isSubmitting && action === 'extension' ? <Spinner size="sm" /> : undefined}
          h="56px"
          borderRadius="999px"
          fontSize="17px"
          fontWeight="600"
          w="full"
        >
          {isSubmitting && action === 'extension'
            ? "Sending..."
            : hasRecentRequest
              ? hasRecentWaitlist
                ? "Waitlist Already Requested"
                : "Extension Already Requested"
              : buttonText
          }
        </Button>

        {(canReject || canCancel) && (
          <VStack spacing="4px" align="center">
            {canReject && (
              <Text {...linkButtonStyle} onClick={handleRejectCollab} disabled={isSubmitting}>
                {isSubmitting && action === 'reject' ? "Rejecting..." : "No Longer Interested? Reject this collab"}
              </Text>
            )}
            {canCancel && (
              <Text {...linkButtonStyle} onClick={handleCancelCollab} disabled={isSubmitting}>
                {isSubmitting && action === 'cancel' ? "Canceling..." : "No Longer Interested? Cancel this collab"}
              </Text>
            )}
          </VStack>
        )}
      </VStack>
    </ModalShell>
  );
};

export default ExtensionRequestModal; 